/*
 * ChangeFeedProvider — genau EINE SSE-Verbindung pro Tab, beliebig viele
 * Subscriber. Events laufen bewusst NICHT durch den Context-Value (sonst
 * würde jedes Event alle Consumer re-rendern), sondern direkt an die
 * registrierten Handler. Der Context-Value bleibt referenzstabil.
 */
import React, { createContext, useContext, useEffect, useMemo, useRef } from "react"

import environment from "@/config/environment"
import { useAccessContext } from "@/contexts/AccessContext"
import { getChanges, openChangeStream } from "@/services/changesApi"
import ChangeFeedClient from "./changeFeedClient"

const ChangeFeedContext = createContext(null)

export function ChangeFeedProvider({ children }) {
  const { accessContext, setAccessContext } = useAccessContext()
  const accessRef = useRef({ accessContext, setAccessContext })
  accessRef.current = { accessContext, setAccessContext }

  const subscribersRef = useRef(new Set())
  const clientRef = useRef(null)

  const value = useMemo(
    () => ({
      subscribe(entityTypes, handler) {
        const entry = { entityTypes: new Set(entityTypes), handler }
        subscribersRef.current.add(entry)
        return () => subscribersRef.current.delete(entry)
      },
    }),
    [],
  )

  // Nur Login/Logout steuern den Client. Token-ROTATION braucht keinen
  // Reconnect: der Server schließt den Stream bei Token-exp (`expired`),
  // und jeder (Re-)Connect holt sich via checkAndRefreshToken ohnehin den
  // frischen Token aus accessRef.
  useEffect(() => {
    if (!environment.change_feed.enabled || !accessContext?.token) {
      clientRef.current?.stop()
      clientRef.current = null
      return
    }
    if (clientRef.current) return

    clientRef.current = new ChangeFeedClient({
      getChanges: (since) =>
        getChanges(since, accessRef.current.accessContext, accessRef.current.setAccessContext),
      openStream: ({ since, signal }) =>
        openChangeStream({
          since,
          accessContext: accessRef.current.accessContext,
          setAccessContext: accessRef.current.setAccessContext,
          signal,
        }),
      onChanges: ({ changes, reset }) => {
        for (const { entityTypes, handler } of subscribersRef.current) {
          if (reset) {
            handler({ entries: [], reset: true })
            continue
          }
          const entries = changes.filter((c) => entityTypes.has(c.entityType))
          if (entries.length) handler({ entries, reset: false })
        }
      },
    })
    clientRef.current.start()
  }, [accessContext?.token])

  useEffect(() => {
    const onVisible = () => {
      if (!document.hidden) clientRef.current?.kick()
    }
    document.addEventListener("visibilitychange", onVisible)
    return () => {
      document.removeEventListener("visibilitychange", onVisible)
      clientRef.current?.stop()
      clientRef.current = null
    }
  }, [])

  return <ChangeFeedContext.Provider value={value}>{children}</ChangeFeedContext.Provider>
}

export const useChangeFeed = () => useContext(ChangeFeedContext)
